import {
  Box,
  MaterialTableWrapper,
  MaterialTableWrapperProps,
  MRT_ColumnDef,
  MRT_PaginationState,
  MRT_TableOptions,
  RowActionButton,
  withThemeProvider,
} from '@ouster/webviz-react-components';

import { useEffect, useMemo, useRef, useState } from 'react';
import { RiMailAddLine } from 'react-icons/ri';

import { AdminDashboardOrgUser } from './types';
import { useAdminDashboardOrgDetail } from './useAdminDashboard';
import useChakraHook from '../../hooks/useChakraHook';
import { useStoreActions } from '../../reducers';
import { formatUnitTimestamp } from '../../utils/misc';

// Helper function to mask email
const maskEmail = (email: string): string => {
  if (!email) return '';

  const atIndex = email.lastIndexOf('@');
  if (atIndex === -1) return email;
  const localPart = email.slice(0, atIndex);
  const domain = email.slice(atIndex + 1);
  if (!localPart || !domain) return email;

  // Show first 2 characters, then asterisks, then last character before @
  const maskedLocal =
    localPart.length > 3
      ? `${localPart.slice(0, 2)}${'*'.repeat(
          localPart.length - 3
        )}${localPart.slice(-1)}`
      : `${localPart[0]}${'*'.repeat(localPart.length - 1)}`;

  // Mask domain but keep the TLD visible
  const domainParts = domain.split('.');
  const maskedDomainName =
    domainParts[0].length > 2
      ? `${domainParts[0].slice(0, 1)}${'*'.repeat(
          domainParts[0].length - 2
        )}${domainParts[0].slice(-1)}`
      : `${'*'.repeat(domainParts[0].length)}`;

  return `${maskedLocal}@${maskedDomainName}.${domainParts.slice(1).join('.')}`;
};

export default function AdminOrgUsers({ orgId }: { orgId: number }) {
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });

  const { setOrgPage, setOrgPageSize } = useStoreActions(
    (actions) => actions.adminDashboard
  );

  const tableContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  }, [pagination.pageIndex]);

  useEffect(() => {
    setOrgPage(pagination.pageIndex);
    setOrgPageSize(pagination.pageSize);
  }, [pagination.pageIndex, pagination.pageSize, setOrgPage, setOrgPageSize]);

  const {
    data: orgDetail,
    isLoading: orgDetailLoading,
    isRefetching,
  } = useAdminDashboardOrgDetail(
    orgId,
    pagination.pageIndex + 1,
    pagination.pageSize
  );

  const columns = useMemo<MRT_ColumnDef<AdminDashboardOrgUser>[]>(
    () => [
      {
        accessorKey: 'username',
        header: 'Name',
      },
      {
        accessorKey: 'email',
        header: 'Email',
        Cell: ({ row }) => {
          const { email, email_opt_out: isOptedOut } = row.original;
          return isOptedOut ? 'Opted out' : maskEmail(email);
        },
      },
      {
        accessorKey: 'role',
        header: 'Role',
      },
      {
        accessorKey: 'total_uploaded_data',
        header: 'Total Uploaded',
      },
      {
        accessorKey: 'creation_date',
        header: 'Start Date',
        Cell: ({ row }) => {
          const createdAt = row.original.creation_date;
          return formatUnitTimestamp(createdAt);
        },
      },
      {
        accessorKey: 'last_access_date',
        header: 'Last Seen',
        Cell: ({ row }) => {
          const lastAccess = row.original.last_access_date;
          return formatUnitTimestamp(lastAccess);
        },
      },
    ],
    []
  );
  const { showToast } = useChakraHook();

  const copyEmailHandler = (row: AdminDashboardOrgUser) => {
    if (row.email && row.email_opt_out !== true) {
      navigator.clipboard
        .writeText(row.email)
        .then(() => {
          showToast({
            title: '',
            description: 'Email copied to clipboard',
            status: 'success',
          });
        })
        .catch((error) => {
          console.error('Failed to copy user email: ', error);
        });
    }
  };

  const users = orgDetail?.users ?? [];
  const { pagination: paging } = orgDetail ?? { paging: { total_users: 0 } };

  const ThemedMaterialTableWrapper =
    withThemeProvider<MaterialTableWrapperProps<AdminDashboardOrgUser>>(
      MaterialTableWrapper
    );

  const tableOptions: MRT_TableOptions<AdminDashboardOrgUser> = {
    columns,
    data: users,
    rowCount: paging?.total_users ?? 0,
    initialState: {
      pagination: { pageSize: 25, pageIndex: 0 },
      columnPinning: { right: ['mrt-row-actions'], left: ['username'] },
      showGlobalFilter: true,
    },
    onPaginationChange: setPagination,
    enableRowActions: true,
    positionGlobalFilter: 'left',
    manualPagination: true,
    enableSorting: true,
    enableToolbarInternalActions: false,
    positionActionsColumn: 'last',
    muiTableContainerProps: {
      ref: tableContainerRef,
      sx: {
        maxHeight: `calc(100% - 120px)`,
      },
    },
    muiPaginationProps: {
      rowsPerPageOptions: [10, 25, 50],
    },
    getRowId: (originalRow) => originalRow.email,
    renderRowActions: ({ row }) => (
      <RowActionButton<AdminDashboardOrgUser>
        title="Copy email"
        icon={<RiMailAddLine />}
        row={row.original}
        onClickHandler={copyEmailHandler}
        isDisabled={!row.original.email || row.original.email_opt_out === true}
      />
    ),
    state: {
      isLoading: orgDetailLoading || isRefetching,
      showLoadingOverlay: false,
      showAlertBanner: false,
      showSkeletons: orgDetailLoading || isRefetching,
      pagination,
    },
  };

  return (
    <Box
      display="grid"
      flexGrow={1}
      gap="1rem"
      p="1rem"
      border="1px solid var(--color-unify-white-40)"
      borderRadius={8}
      height="100%"
      overflow="hidden"
      data-testid="admin-org-users-box"
    >
      <ThemedMaterialTableWrapper {...tableOptions} />
    </Box>
  );
}
